import { phTrack } from "../analytics/productAnalytics";
import type { PhProps } from "../analytics/productAnalytics";
import { mapGa4EcommerceToPosthog } from "../analytics/posthogEvents";

export type Ga4Item = {
  item_id?: string | number;
  sku?: string;
  item_name?: string;
  item_brand?: string;
  item_variant?: string;
  index?: number;
  price: number;
  discount?: number;
  coupon?: string;
  sale_discount?: number;
  coupon_discount?: number;
  quantity: number;
};

export const parseGa4Price = (value: unknown): number => {
  const parsed = Number(String(value ?? "").replace(/[^0-9.\-]+/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

export const resolveGa4ProductPrice = (
  product: any,
  line: any = null
): number => {
  const special = product?.special ?? line?.special;
  if (special !== null && special !== undefined && String(special).trim()) {
    return parseGa4Price(special);
  }

  const productPrice = product?.price ?? line?.price;
  if (
    productPrice !== null &&
    productPrice !== undefined &&
    String(productPrice).trim()
  ) {
    return parseGa4Price(productPrice);
  }

  const quantity = Math.max(1, Number(line?.quantity) || 1);
  return parseGa4Price(line?.total) / quantity;
};

export const cartLineToGa4Item = (line: any, index?: number): Ga4Item => {
  const product = line?.product || line || {};
  const quantity = Math.max(1, Number(line?.quantity) || 1);
  const price = resolveGa4ProductPrice(product, line);
  const basePrice = parseGa4Price(
    product?.original_price ?? product?.price ?? line?.price
  );

  return {
    item_id: product?.id ?? product?.productId,
    ...(product?.sku !== null && product?.sku !== undefined
      ? { sku: String(product.sku) }
      : {}),
    item_name: product?.name,
    ...(product?.manufacturer?.name
      ? { item_brand: product.manufacturer.name }
      : {}),
    ...(product?.model ? { item_variant: product.model } : {}),
    ...(typeof index === "number" ? { index } : {}),
    price,
    discount: Math.max(0, basePrice - price),
    quantity,
  };
};

export const cartToGa4Items = (cart: any): Ga4Item[] => {
  const items = (Array.isArray(cart?.products) ? cart.products : []).map(
    (line: any, index: number) => cartLineToGa4Item(line, index)
  );
  const totals = Array.isArray(cart?.totals) ? cart.totals : [];
  const grossCents = items.map((item) =>
    Math.round(item.price * item.quantity * 100)
  );
  const subtotal = grossCents.reduce((sum, cents) => sum + cents, 0);
  const discount = Math.min(
    subtotal,
    Math.round(
      totals
        .filter((total) => total.code === "coupon")
        .reduce(
          (sum, total) => sum + Math.max(0, -parseGa4Price(total.value)),
          0
        ) * 100
    )
  );
  const coupon = getGa4CouponCode(cart);
  let allocated = 0;
  let cumulative = 0;
  return items.map((item, index) => {
    cumulative += grossCents[index];
    // Allocate in cents by cumulative proportions: the last line closes the
    // rounding remainder and zero-price lines never receive a discount.
    const target = subtotal
      ? Math.round((discount * cumulative) / subtotal)
      : 0;
    const lineDiscount = target - allocated;
    allocated = target;
    const couponDiscount = lineDiscount / 100 / item.quantity;
    const saleDiscount = item.discount || 0;
    return {
      ...item,
      price: Number(
        ((grossCents[index] - lineDiscount) / 100 / item.quantity).toFixed(6)
      ),
      discount: Number((saleDiscount + couponDiscount).toFixed(6)),
      sale_discount: saleDiscount,
      coupon_discount: Number(couponDiscount.toFixed(6)),
      ...(coupon && lineDiscount > 0 ? { coupon } : {}),
    };
  });
};

export const cartToGa4Ecommerce = (cart: any) => {
  const items = cartToGa4Items(cart);
  const coupon = getGa4CouponCode(cart);
  const totals = Array.isArray(cart?.totals) ? cart.totals : [];
  const totalFor = (code: string) =>
    Number(
      totals
        .filter((total) => total.code === code)
        .reduce((sum, total) => sum + parseGa4Price(total.value), 0)
        .toFixed(2)
    );
  return {
    currency: "USD",
    value: ga4ItemsValue(items),
    items,
    shipping: totalFor("shipping"),
    tax: totalFor("tax"),
    ...(coupon ? { coupon } : {}),
  };
};

export const ga4ItemsValue = (items: Ga4Item[]): number =>
  Number(
    items
      .reduce(
        (sum, item) =>
          sum + (Number(item.price) || 0) * (Number(item.quantity) || 0),
        0
      )
      .toFixed(2)
  );

export const getGa4CouponCode = (cart: any): string | undefined => {
  try {
    const candidates = Array.isArray(cart?.totals) ? cart.totals : [];
    for (const candidate of candidates) {
      if (candidate?.code && candidate.code !== "coupon") continue;
      if (!candidate?.data) continue;
      const data =
        typeof candidate.data === "string"
          ? JSON.parse(candidate.data)
          : candidate.data;
      const code = data?.coupon_code || data?.name;
      if (code) return String(code);
    }
  } catch {
    // Invalid optional coupon metadata must not block checkout analytics.
  }
  return undefined;
};

/**
 * Mirrors one GA4 ecommerce event to PostHog through the C4 mapping. Events
 * PostHog takes from elsewhere (add/remove to cart, begin_checkout, purchase)
 * map to nothing. It never throws and never touches the GA4 payload.
 */
export const forwardGa4EcommerceToPosthog = (
  event: string,
  ecommerce: Record<string, unknown>,
  posthogProps?: PhProps
): void => {
  try {
    const mapped = mapGa4EcommerceToPosthog(event, ecommerce, posthogProps);
    if (mapped) phTrack(mapped.event, mapped.props);
  } catch {
    // Product analytics must never interrupt GA4 or the storefront.
  }
};

export const pushGa4EcommerceEvent = (
  event: string,
  ecommerce: Record<string, unknown>,
  posthogProps?: PhProps
): boolean => {
  if (
    typeof window === "undefined" ||
    !Array.isArray((window as any).dataLayer)
  ) {
    return false;
  }

  (window as any).dataLayer.push({ ecommerce: null });
  (window as any).dataLayer.push({ event, ecommerce });
  forwardGa4EcommerceToPosthog(event, ecommerce, posthogProps);
  return true;
};

let lastGa4ViewCartSignature = "";

export const resetGa4ViewCartTracking = (): void => {
  lastGa4ViewCartSignature = "";
};

export const pushGa4ViewCart = (
  signature: string,
  ecommerce: Record<string, unknown>
): boolean => {
  if (!signature || signature === lastGa4ViewCartSignature) return false;
  // Only the bag drawer uses this helper; the cart page pushes its own.
  if (!pushGa4EcommerceEvent("view_cart", ecommerce, { surface: "drawer" }))
    return false;
  lastGa4ViewCartSignature = signature;
  return true;
};
